import React from 'react';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';
import { StyledTextField } from 'components/Input/styles';

import { ConfirmationType } from './types';
import { ModalContent, ModalText, HelpText } from './styles';

const CONFIRMATION_TEXT = 'DELETE';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  type: ConfirmationType;
  title?: string;
  message?: string | React.ReactNode;
  confirmText?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  hideRecoveryWarning?: boolean;
  loading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  type,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item?',
  confirmText = CONFIRMATION_TEXT,
  confirmButtonLabel = 'Delete',
  cancelButtonLabel = 'Cancel',
  hideRecoveryWarning = false,
  loading = false,
}) => {
  const [inputValue, setInputValue] = React.useState('');

  const isTextInputType = type === 'text-input';
  const isConfirmDisabled = isTextInputType && inputValue !== confirmText;

  const handleConfirm = async (): Promise<void> => {
    await onConfirm();
    setInputValue('');
  };

  const handleClose = (): void => {
    if (!loading) {
      setInputValue('');
      onClose();
    }
  };

  return (
    <CustomModal
      openModal={open}
      onClose={handleClose}
      modalTitle={title}
      modalWidth={isTextInputType ? 'sm' : 'xs'}
      cancelButton={
        <Button disabled={loading} onClick={handleClose} autoFocus id="cancel-button" variant="outlined" size="large">
          {cancelButtonLabel}
        </Button>
      }
      submitButton={
        <Button
          onClick={handleConfirm}
          disabled={isConfirmDisabled || loading}
          id="confirm-delete-button"
          variant="contained"
          size="large"
          color="error"
        >
          {confirmButtonLabel}
        </Button>
      }
    >
      <ModalContent>
        {typeof message === 'string' ? <ModalText>{message}</ModalText> : message}
        {!hideRecoveryWarning && <ModalText>Once removed, you will not be able to recover this data.</ModalText>}
        {isTextInputType && (
          <>
            <HelpText>
              Please type in <b>{confirmText}</b> to confirm
            </HelpText>
            <StyledTextField
              variant="outlined"
              placeholder={`Please type in ${confirmText} to confirm`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
          </>
        )}
      </ModalContent>
    </CustomModal>
  );
};

export default ConfirmationModal;
